import React from 'react';
import { toast } from 'react-toastify';
import { mockUser } from './mock';
import {
    signIn as cognitoSignIn,
    signOut as cognitoSignOut,
    getCurrentUser as getCognitoUser,
    fetchUserAttributes,
    fetchAuthSession,
    confirmSignIn,
} from 'aws-amplify/auth';

import config from '../../src/config';

var UserStateContext = React.createContext();
var UserDispatchContext = React.createContext();

// Gets the user's group from the cognito token that defines the territory for the user
const getCognitoGroup = (data) => {
    const groups = data?.idTokenPayload?.['cognito:groups'] || [];
    const isAdmin = groups.includes('admin');
    const group = isAdmin ? groups.find((g) => g !== 'admin') : groups[0];
    return group;
};

// Use localStorage for isAuthenticated to avoid re-routing after a page refresh
const initialData = {
    isLoading: false,
    isAuthenticated: localStorage.getItem('isAuthenticated'),
    isOrtho: false,
    currentUser: null,
    territory: null,
    errorMessage: '',
};

function userReducer(state, { type, payload }) {
    switch (type) {
        case 'AUTH_STARTED':
            return { ...state, isLoading: true, errorMessage: '' };
        case 'LOGIN_SUCCESS':
            return { ...state, currentUser: payload, isLoading: false };
        case 'AUTH_SUCCESS':
            return {
                ...state,
                isAuthenticated: true,
                currentUser: payload,
                isOrtho: true, // always assume the admin is an ortho
                territory: getCognitoGroup(payload),
                isLoading: false,
            };
        case 'AUTH_ERROR':
            return {
                ...state,
                isAuthenticated: false,
                currentUser: null,
                isLoading: false,
                territory: null,
                errorMessage: payload,
            };
        case 'RESET_PASSWORD_STARTED':
            return { ...state, isLoading: true, errorMessage: '' };
        case 'RESET_PASSWORD_SUCCESS':
            return { ...state, isLoading: false, errorMessage: '' };
        case 'RESET_PASSWORD_ERROR':
            return { ...state, isLoading: false, errorMessage: payload };
        case 'SIGN_OUT_SUCCESS':
            return { ...initialData, isAuthenticated: localStorage.getItem('isAuthenticated') };
        default: {
            return state;
        }
    }
}

function UserProvider({ children }) {
    var [state, dispatch] = React.useReducer(userReducer, initialData);

    return (
        <UserStateContext.Provider value={state}>
            <UserDispatchContext.Provider value={dispatch}>{children}</UserDispatchContext.Provider>
        </UserStateContext.Provider>
    );
}

function useUserState() {
    var context = React.useContext(UserStateContext);
    if (context === undefined) {
        throw new Error('useUserState must be used within a UserProvider');
    }
    return context;
}

function useUserDispatch() {
    var context = React.useContext(UserDispatchContext);
    if (context === undefined) {
        throw new Error('useUserDispatch must be used within a UserProvider');
    }
    return context;
}

// ###########################################################

const actions = {
    signIn: (email, password, history) => async (dispatch) => {
        dispatch({ type: 'AUTH_STARTED' });
        if (!config.isBackend) {
            setTimeout(() => {
                localStorage.setItem('isAuthenticated', true);
                actions.getCurrentUser(history)(dispatch);
            }, 1000);
        } else {
            try {
                const { isSignedIn, nextStep } = await cognitoSignIn({ username: email, password });
                if (!isSignedIn && nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                    dispatch({ type: 'LOGIN_SUCCESS', payload: nextStep });
                    history.push('/password-reset');
                } else {
                    localStorage.setItem('isAuthenticated', true);
                    actions.getCurrentUser(history)(dispatch);
                }
            } catch (error) {
                console.log('Unable to login', error);
                dispatch({ type: 'AUTH_ERROR', payload: 'Something is wrong with your login or password' });
            }
        }
    },

    signOut: (history) => async (dispatch) => {
        if (config.isBackend) cognitoSignOut();
        localStorage.removeItem('isAuthenticated');
        dispatch({ type: 'SIGN_OUT_SUCCESS' });
        history.push('/login');
    },

    getCurrentUser: (history) => async (dispatch) => {
        if (!config.isBackend) return dispatch({ type: 'AUTH_SUCCESS', payload: mockUser });
        try {
            const [cognitoUser, attributes, session] = await Promise.all([
                getCognitoUser(),
                fetchUserAttributes(),
                fetchAuthSession(),
            ]);
            const payload = {
                ...cognitoUser,
                attributes,
                idTokenPayload: session.tokens?.idToken?.payload,
            };
            dispatch({ type: 'AUTH_SUCCESS', payload });
        } catch (error) {
            console.log('Unable to get current user, signing out');
            actions.signOut(history)(dispatch);
        }
    },

    /**
     * Completes the new-password challenge for the in-progress sign-in
     * @param user truthy marker that a challenge is pending (v6 tracks the challenge state internally)
     * @param password the new password
     */
    completeNewPassword: (user, password, history) => async (dispatch) => {
        if (!config.isBackend || !user) return history.push('/login');
        try {
            dispatch({ type: 'RESET_PASSWORD_STARTED' });
            // confirmSignIn completes authentication directly, no separate re-sign-in needed
            await confirmSignIn({ challengeResponse: password });

            dispatch({ type: 'RESET_PASSWORD_SUCCESS' });
            toast.success('Your password has been updated');

            localStorage.setItem('isAuthenticated', true);
            actions.getCurrentUser(history)(dispatch);
        } catch (error) {
            console.log('Unable to change password', error);
            dispatch({ type: 'RESET_PASSWORD_ERROR', payload: error.message });
        }
    },
};

export { UserProvider, useUserState, useUserDispatch, actions };
